import Papa from 'papaparse'
import ExcelJS from 'exceljs'
import { format } from 'date-fns'

/** CSV/Excelヘッダーのキーワード定義 */
const CSV_HEADER_KEYWORDS = {
  BEFORE_JP: '変換前',
  AFTER_JP: '変換後',
  BEFORE_EN: 'before',
  AFTER_EN: 'after'
}

/** ヘッダー検出用のマーカー配列 */
const HEADER_MARKERS = Object.values(CSV_HEADER_KEYWORDS)

/**
 * 行がヘッダー行かどうかを判定
 *
 * @param {Array | Object} row - CSV行データ
 * @returns {boolean} ヘッダー行の場合true
 */
const hasHeaderRow = (row) => {
  const cells = Array.isArray(row) ? row : Object.values(row)
  return cells.some((cell) =>
    HEADER_MARKERS.some((marker) => String(cell).trim().toLowerCase().includes(marker.toLowerCase()))
  )
}

/**
 * 行データから変換前・変換後の値を抽出
 *
 * @param {Array | Object} row - CSV行データ
 * @returns {{ before: string; after: string }} 変換前・変換後の値
 */
const extractCells = (row) => {
  if (Array.isArray(row)) {
    return {
      before: (row[0] != null ? String(row[0]) : '').trim(),
      after: (row[1] != null ? String(row[1]) : '').trim()
    }
  }

  if (row && typeof row === 'object') {
    const keys = Object.keys(row)
    return {
      before: (row[keys[0]] != null ? String(row[keys[0]]) : '').trim(),
      after: (row[keys[1]] != null ? String(row[keys[1]]) : '').trim()
    }
  }

  return { before: '', after: '' }
}

/** インポートヘッダー不正時にスローするエラー識別子 */
export const IMPORT_HEADER_ERROR = 'INVALID_HEADER'

/**
 * CSV の1行目がテンプレート形式のヘッダー（変換前・変換後）か検証する。不正なら INVALID_HEADER をスロー
 *
 * @param {string} text - CSV テキスト
 * @throws {Error} 1行目がヘッダーでない場合
 */
export function validateFirstRowIsHeaderCSV(text) {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const parsed = Papa.parse(normalized, { skipEmptyLines: true })
  if (!parsed.data || !parsed.data.length) {
    throw new Error(IMPORT_HEADER_ERROR)
  }
  if (!hasHeaderRow(parsed.data[0])) {
    throw new Error(IMPORT_HEADER_ERROR)
  }
}

/**
 * Excel の指定シート1行目がテンプレート形式のヘッダー（変換前・変換後）か検証する。不正なら INVALID_HEADER をスロー
 *
 * @param {ArrayBuffer} buffer - ExcelファイルのArrayBuffer
 * @param {string} [sheetName] - シート名（省略時は先頭シート）
 * @returns {Promise<void>}
 * @throws {Error} 1行目がヘッダーでない場合
 */
export async function validateFirstRowIsHeaderExcel(buffer, sheetName) {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(buffer)
  const sheet = sheetName ? workbook.getWorksheet(sheetName) : workbook.worksheets[0]
  if (!sheet) {
    throw new Error(IMPORT_HEADER_ERROR)
  }
  const firstRow = sheet.getRow(1)
  const cells = firstRow?.values
  if (!cells || cells.length < 2) {
    throw new Error(IMPORT_HEADER_ERROR)
  }
  const before = (cells[1] != null ? String(cells[1]) : '').trim()
  const after = (cells[2] != null ? String(cells[2]) : '').trim()
  const isHeader = HEADER_MARKERS.some(
    (m) => before.toLowerCase().includes(m.toLowerCase()) || after.toLowerCase().includes(m.toLowerCase())
  )
  if (!isHeader) {
    throw new Error(IMPORT_HEADER_ERROR)
  }
}

/**
 * CSV テキストを解析し、{ id, before, after } の配列を返す 1行目が「変換前」「変換後」等を含む場合はヘッダとしてスキップ
 *
 * @param {string} text - CSV テキスト
 * @returns {import('./types').ConversionItem[]}
 */
export function parseCSV(text) {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const parsed = Papa.parse(normalized, { skipEmptyLines: true })

  if (!parsed.data || !parsed.data.length) return []

  const rows = parsed.data
  const startIndex = hasHeaderRow(rows[0]) ? 1 : 0

  const result = []
  const timestamp = Date.now()
  for (let i = startIndex; i < rows.length; i++) {
    const { before, after } = extractCells(rows[i])
    if (before === '' && after === '') continue
    result.push({
      id: `${before}_${after}_${i}_${timestamp}`,
      before,
      after
    })
  }

  return result
}

/**
 * Excel の ArrayBuffer から指定シートを解析し、{ before, after } の配列を返す
 *
 * @param {ArrayBuffer} buffer - ExcelファイルのArrayBuffer
 * @param {string} [sheetName] - シート名（省略時は先頭シート）
 * @returns {Promise<import('./types').ConversionItem[]>}
 */
export async function parseExcel(buffer, sheetName) {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(buffer)
  const sheet = sheetName ? workbook.getWorksheet(sheetName) : workbook.worksheets[0]
  if (!sheet) return []

  const rows = []
  const timestamp = Date.now()
  sheet.eachRow((row, rowNumber) => {
    const cells = row.values
    if (!cells || cells.length < 2) return
    const before = (cells[1] != null ? String(cells[1]) : '').trim()
    const after = (cells[2] != null ? String(cells[2]) : '').trim()
    if (rowNumber === 1) {
      const hasHeader = HEADER_MARKERS.some(
        (m) => before.toLowerCase().includes(m.toLowerCase()) || after.toLowerCase().includes(m.toLowerCase())
      )
      if (hasHeader) return
    }
    if (before === '' && after === '') return
    rows.push({
      id: `${before}_${after}_${rowNumber}_${timestamp}`,
      before,
      after
    })
  })
  return rows
}

/**
 * Excel ワークブックのシート名一覧を返す
 *
 * @param {ArrayBuffer} buffer - ExcelファイルのArrayBuffer
 * @returns {Promise<string[]>} シート名の配列
 */
export async function getExcelSheetNames(buffer) {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(buffer)
  return workbook.worksheets.map((ws) => ws.name)
}

/**
 * 変換前の値の重複チェック
 *
 * @param {import('./types').ConversionItem[]} rows - 変換項目の配列
 * @returns {import('./types').ValidationError[]} バリデーションエラーの配列
 */
export function validateDuplicateBefore(rows) {
  const seen = new Map()
  const errors = []
  rows.forEach((row, index) => {
    const v = row.before
    if (seen.has(v)) {
      errors.push({
        rowIndex: index + 1,
        errorType: 'duplicate',
        field: 'before',
        value: v,
        message: '値が重複しています'
      })
    } else {
      seen.set(v, true)
    }
  })
  return errors
}

/**
 * 変換後の値が区分値リストに含まれるかチェック
 *
 * @param {import('./types').ConversionItem[]} rows - 変換項目の配列
 * @param {string[]} validValues - 有効な区分値リスト
 * @returns {import('./types').ValidationError[]} バリデーションエラーの配列
 */
export function validateAfterInClassification(rows, validValues) {
  if (!validValues || validValues.length === 0) return []
  const set = new Set(validValues.map((v) => String(v)))
  const errors = []
  rows.forEach((row, index) => {
    const v = row.after
    if (v !== '' && !set.has(v)) {
      errors.push({
        rowIndex: index + 1,
        errorType: 'invalid',
        field: 'after',
        value: v,
        message: '変換後の値が区分値に存在しません'
      })
    }
  })
  return errors
}

/**
 * CSV 用：値をエスケープ（カンマ・ダブルクォート・改行を含む場合はクォート）
 *
 * @param {string} s - エスケープする文字列
 * @returns {string} エスケープされた文字列
 */
function escapeCSVValue(s) {
  const str = String(s)
  if (/[,"\n\r]/.test(str)) {
    return '"' + str.replace(/"/g, '""') + '"'
  }
  return str
}

/**
 * 変換項目リストを CSV 文字列に変換（BOM + ヘッダ + データ）
 *
 * @param {import('./types').ConversionItem[]} items - 変換項目の配列
 * @returns {string} CSV形式の文字列
 */
export function toCSVString(items) {
  const header = '変換前,変換後'
  const lines = items.map((item) => `${escapeCSVValue(item.before)},${escapeCSVValue(item.after)}`)
  return '\uFEFF' + header + '\n' + lines.join('\n')
}

/**
 * CSV を Blob でダウンロード
 *
 * @param {{ before: string; after: string }[]} items
 * @param {string} [filename] - ファイル名（拡張子なし可）
 */
export function downloadCSV(items, filename) {
  const name = filename || `conversion_table_${formatTimestamp()}.csv`
  const csv = toCSVString(items)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name.endsWith('.csv') ? name : name + '.csv'
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * 変換項目リストを Excel でダウンロード
 *
 * @param {import('./types').ConversionItem[]} items - 変換項目の配列
 * @param {string} [filename] - ファイル名（拡張子なし可）
 * @returns {Promise<void>}
 */
export async function downloadExcel(items, filename) {
  const name = (filename || `conversion_table_${formatTimestamp()}`).replace(/\.xlsx$/i, '')
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet('変換表', { views: [{ state: 'frozen', ySplit: 1 }] })
  sheet.addRow(['変換前', '変換後'])
  items.forEach((item) => sheet.addRow([item.before, item.after]))
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name + '.xlsx'
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * YYYYMMDDHHmmss 形式のタイムスタンプを生成
 *
 * @param {Date} [date=new Date()] - 日時オブジェクト（省略時は現在時刻）. Default is `new Date()`
 * @returns {string} YYYYMMDDHHmmss形式の文字列
 */
export function formatTimestamp(date = new Date()) {
  return format(date, 'yyyyMMddHHmmss')
}

/**
 * API 変換項目リストを画面用 { id, before, after } に変換
 *
 * @param {import('./types').ApiConversionItem[]} list - APIレスポンスの変換項目リスト
 * @returns {import('./types').ConversionItem[]} 画面用の変換項目リスト
 */
export function apiConversionListToItems(list) {
  if (!Array.isArray(list)) return []
  const timestamp = Date.now()
  return list.map((item, index) => ({
    id: `${item.value_before ?? ''}_${item.value_after ?? ''}_${index}_${timestamp}`,
    before: item.value_before ?? '',
    after: item.value_after ?? ''
  }))
}

/**
 * 画面用変換項目を API 用 conversion_table_items に変換（display_order は 1 から連番）
 *
 * @param {import('./types').ConversionItem[]} items - 画面用の変換項目リスト
 * @returns {import('./types').ApiConversionItem[]} API用の変換項目リスト
 */
export function itemsToApiConversionList(items) {
  if (!Array.isArray(items)) return []
  return items.map((item, index) => ({
    value_before: item.before,
    value_after: item.after,
    display_order: index + 1
  }))
}

/**
 * 変換表の表示順を取得
 *
 * @param {Object} table - 変換表オブジェクト
 * @returns {number | undefined} 表示順
 */
export const getTableDisplayOrder = (table) =>
  table.conversion_table_display_order ?? table.ConversionTableDisplayOrder ?? table.display_order

/**
 * 変換表のキーを取得
 *
 * @param {Object} table - 変換表オブジェクト
 * @returns {string} 変換表キー
 */
export const getTableKey = (table) => table.conversion_table_key ?? table.ConversionTableKey ?? table.key

/**
 * 変換表名を取得
 *
 * @param {Object} table - 変換表オブジェクト
 * @returns {string} 変換表名
 */
export const getTableName = (table) => table.conversion_table_name ?? table.ConversionTableName ?? table.name ?? ''

/**
 * 変換表のレコード数を取得
 *
 * @param {Object} table - 変換表オブジェクト
 * @returns {number} レコード数
 */
export const getTableCount = (table) =>
  table.conversion_table_record_count ?? table.ConversionTableRecordCount ?? table.recordCount ?? 0

/**
 * 変換表の変換先区分を取得
 *
 * @param {Object} table - 変換表オブジェクト
 * @returns {string} 変換先区分
 */
export const getTableTarget = (table) =>
  table.conversion_target ?? table.ConversionTarget ?? table.conversionTarget ?? ''

/**
 * 変換表のメモを取得
 *
 * @param {Object} table - 変換表オブジェクト
 * @returns {string} メモ
 */
export const getTableMemo = (table) => table.conversion_table_memo ?? table.ConversionTableMemo ?? table.memo ?? ''
